import { Control } from "Controls/Control";

/**
 * Control for selecting colors (hex codes) with a color picker.
 * @since 4.0.0
 * @example 
 * ```JavaScript
 * // Access the instance of an ColorPicker with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 * 
 * // On page:
 * $dms(1337)
 * ```
 */
export class ColorPicker extends Control {
    private _kendoElement: kendo.ui.ColorPicker;

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-colorpicker";

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this._kendoElement = new kendo.ui.ColorPicker(this.jQueryElement[0], {
            buttons: false,
            clearButton: true
        });
    }

    /**
     * Gets the display template for formatting in grid row.
     * @hidden
     */
    public getDisplayTemplate(bindingField?: string): string {
        return `#= ${bindingField} == null ? "${this.controlDefinition.ReplaceNullValue || ""}"  : "<div class='dms-colorpicker-display' style='border-color:" + ${bindingField} + ";background-color:" + ${bindingField} + ";'>" # `;
    }

    /**
     * Gets template for showing tooltips.
     * @param bindingField Bound field in the datarow.
     * @hidden
     */
    protected getTooltipTemplate(bindingField?: string): string {
        return `#= ${bindingField} == null ? "${this.controlDefinition.ReplaceNullValue || ""}"  : ${bindingField} # `;
    }

    /**
     * Set control value.
     * @param value Hex code for the color.
     * @since 4.0.0
     */
    public setValue(value: string) {
        let oldValue = this._kendoElement.value();
        if (oldValue !== value) {
            this._kendoElement.value(value);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Get the value of the control.
     * @since 4.0.0
     */
    public getValue(): string {
        return this._kendoElement.value();
    }

    /**
     * Enable the control.
     * @since 4.0.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.0.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Returns true if control is enabled.
     * @since 4.0.0
     */
    public isEnabled(): boolean {
        return !this._kendoElement.element.prop("disabled");
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @hidden
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        try {
            if (this._kendoElement) {
                this._kendoElement.destroy();
            }
            if (this.jQueryElement) {
                this.jQueryElement.off("click");
                this.jQueryElement.off("focusout");
            }
        } catch (e) {

        }
    }
}
